import * as React from "react";
import {
  Typography,
  Paper,
  IconButton,
  InputBase,
  Box,
  Container,
  Pagination,
  MenuItem,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Select,
  FormHelperText,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  TextField,
  Modal,
  Divider,
} from "@mui/material";
import { connect } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  centered_flex_box,
  MainInput,
  MainInputLabel,
  main_button,
  sec_button,
  StyledInput,
} from "../../app/components/Styles";
import moment from "moment";
import { EventCard, TicketCard } from "../../app/components";
import {
  getWhatsappEventInvitations,
  downloadEventInvitations,
  manuallyAcceptInvitation
} from "../../app/store/actions/adminActions";
import { getEvent } from "../../app/store/actions/eventActions";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformCard,
  PlatformStatsGrid,
  PlatformStatCard,
  PlatformSearchBar,
  PlatformFilterChip,
  PlatformButton,
  PlatformAvatar,
  PlatformBadge,
  PlatformEmptyState,
  PlatformLoadingBlock,
  getInitials,
  pickAvatarPalette,
  invitationStatusBadge,
  messageStatusBadge,
} from "../../app/components/platform";
import {
  platformColors,
  platformFilterSelectSx,
  platformPrimaryButtonSx,
  platformGhostButtonSx,
  platformTableSx,
  platformTableContainerSx,
  platformFonts,
} from "../../app/components/tickitPlatformTheme";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const InvitationRow = ({
  invitation,
  onInfoClick,
  onManualAction,
  isProcessing,
  eventStarted,
}) => {
  const guestName = invitation.userInfo?.name || "Guest";
  const palette = pickAvatarPalette(guestName);
  const statusBadge = invitationStatusBadge(invitation.status);
  const deliveryBadge = messageStatusBadge(invitation.messageStatus);
  const contact =
    invitation.userInfo?.phoneNumber ||
    invitation.userInfo?.email ||
    "No contact";

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <PlatformAvatar
            initials={getInitials(guestName)}
            bg={palette.bg}
            color={palette.color}
            size={34}
          />
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8125rem",
                color: 'var(--platform-text)',
                fontFamily: platformFonts.body,
              }}
            >
              {guestName}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: 'var(--platform-text-3)', fontFamily: platformFonts.body }}>
              {contact}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <PlatformBadge variant={invitation.category ? "purple" : "gray"}>
          {invitation.category || "Regular"}
        </PlatformBadge>
      </TableCell>
      <TableCell>
        <PlatformBadge variant={statusBadge.variant}>{statusBadge.label}</PlatformBadge>
      </TableCell>
      <TableCell>
        <PlatformBadge variant={deliveryBadge.variant}>{deliveryBadge.label}</PlatformBadge>
      </TableCell>
      <TableCell>
        <Typography sx={{ fontSize: "0.75rem", color: 'var(--platform-text-3)', fontFamily: platformFonts.body }}>
          {invitation.status === "Unseen"
            ? "No response yet"
            : moment(invitation.updatedAt || invitation.createdAt).fromNow()}
        </Typography>
      </TableCell>
      <TableCell>
        {(invitation.status === "Unseen" || invitation.status === "Accepted") ? (
          eventStarted ? (
            <Typography sx={{ fontSize: "0.75rem", color: 'var(--platform-text-3)', maxWidth: 140 }}>
              Event started
            </Typography>
          ) : (
            <PlatformButton
              variant={invitation.messageStatus === "failed" ? "danger" : invitation.status === "Accepted" ? "ghost" : "primary"}
              size="small"
              onClick={() => onManualAction(invitation)}
              disabled={isProcessing}
            >
              {isProcessing
                ? "Processing…"
                : invitation.status === "Accepted"
                  ? "Resend QR"
                  : invitation.messageStatus === "failed"
                    ? "Retry"
                    : "Accept"}
            </PlatformButton>
          )
        ) : (
          <Typography sx={{ fontSize: "0.75rem", color: 'var(--platform-text-3)' }}>—</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <PlatformButton variant="ghost" size="small" onClick={() => onInfoClick(invitation)}>
          View
        </PlatformButton>
      </TableCell>
    </TableRow>
  );
};

export const EventInvitations = ({
  invitations,
  isLoading,
  getWhatsappEventInvitations,
  downloadEventInvitations,
  manuallyAcceptInvitation,
  getEvent,
  event
}) => {
  const { id } = useParams();
  const [action, setAction] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [infoModalOpen, setInfoModalOpen] = React.useState(false);
  const [selectedInvitation, setSelectedInvitation] = React.useState(null);
  const [processingInvitationId, setProcessingInvitationId] = React.useState(null);

  const eventStarted =
    !!(event?.date && moment().isSameOrAfter(moment(event.date)));

  React.useEffect(() => {
    getEvent({ eventId: id });
  }, [id]);

  const initialState = {
    eventId: id,
    messageStatus: "any",
    ticketMessageStatus: "any",
    status: "any",
    category: "any",
    search: "",
    page: 1,
    limit: 50,
  };

  React.useEffect(() => {
    // Filter out "any" values before sending to API
    const filteredData = { ...initialState };
    if (filteredData.messageStatus === "any") {
      delete filteredData.messageStatus;
    }
    if (filteredData.ticketMessageStatus === "any") {
      delete filteredData.ticketMessageStatus;
    }
    if (filteredData.status === "any") {
      delete filteredData.status;
    }
    if (filteredData.category === "any") {
      delete filteredData.category;
    }
    if (!filteredData.search) {
      delete filteredData.search;
    }
    getWhatsappEventInvitations(filteredData);
  }, []);

  const [formData, setFormData] = React.useState(initialState);
  const { messageStatus, ticketMessageStatus, status, category } = formData;

  const handleClearFilter = (event) => {
    setFormData(initialState);
    setSearchQuery("");
    
    // Filter out "any" values before sending to API
    const filteredData = { ...initialState };
    if (filteredData.messageStatus === "any") {
      delete filteredData.messageStatus;
    }
    if (filteredData.ticketMessageStatus === "any") {
      delete filteredData.ticketMessageStatus;
    }
    if (filteredData.status === "any") {
      delete filteredData.status;
    }
    if (filteredData.category === "any") {
      delete filteredData.category;
    }
    if (!filteredData.search) {
      delete filteredData.search;
    }
    getWhatsappEventInvitations(filteredData);
  };

  const { page } = formData;

  const handlePageChange = (event, value) => {
    const newFormData = { ...formData, page: value };
    setFormData(newFormData);
    
    // Filter out "any" values before sending to API
    const filteredData = { ...newFormData };
    if (filteredData.messageStatus === "any") {
      delete filteredData.messageStatus;
    }
    if (filteredData.ticketMessageStatus === "any") {
      delete filteredData.ticketMessageStatus;
    }
    if (filteredData.status === "any") {
      delete filteredData.status;
    }
    if (filteredData.category === "any") {
      delete filteredData.category;
    }
    if (!filteredData.search) {
      delete filteredData.search;
    }
    getWhatsappEventInvitations(filteredData);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setFormData({ ...formData, search: event.target.value });
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearchFilter(event);
    }
  };

  const handleSearchFilter = (event) => {
    // Filter out "any" values before sending to API
    const filteredData = { ...formData };
    if (filteredData.messageStatus === "any") {
      delete filteredData.messageStatus;
    }
    if (filteredData.ticketMessageStatus === "any") {
      delete filteredData.ticketMessageStatus;
    }
    if (filteredData.status === "any") {
      delete filteredData.status;
    }
    if (filteredData.category === "any") {
      delete filteredData.category;
    }
    if (!filteredData.search) {
      delete filteredData.search;
    }
    getWhatsappEventInvitations(filteredData);
  };

  React.useEffect(() => {
    // Filter out "any" values before sending to API
    const filteredData = { ...formData };
    if (filteredData.messageStatus === "any") {
      delete filteredData.messageStatus;
    }
    if (filteredData.ticketMessageStatus === "any") {
      delete filteredData.ticketMessageStatus;
    }
    if (filteredData.status === "any") {
      delete filteredData.status;
    }
    if (filteredData.category === "any") {
      delete filteredData.category;
    }
    if (!filteredData.search) {
      delete filteredData.search;
    }
    getWhatsappEventInvitations(filteredData);
  }, [action]);

  const handleInfoClick = (invitation) => {
    setSelectedInvitation(invitation);
    setInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModalOpen(false);
    setSelectedInvitation(null);
  };

  const handleManualAction = async (invitation) => {
    setProcessingInvitationId(invitation._id);
    try {
      await manuallyAcceptInvitation({ invitationId: invitation._id });
      setAction((prev) => !prev);
    } finally {
      setProcessingInvitationId(null);
    }
  };

  const buildFilteredPayload = (data) => {
    const filteredData = { ...data };
    if (filteredData.messageStatus === "any") delete filteredData.messageStatus;
    if (filteredData.ticketMessageStatus === "any") delete filteredData.ticketMessageStatus;
    if (filteredData.status === "any") delete filteredData.status;
    if (filteredData.category === "any") delete filteredData.category;
    if (!filteredData.search) delete filteredData.search;
    return filteredData;
  };

  const applyFilters = (overrides = {}) => {
    const next = { ...formData, ...overrides };
    setFormData(next);
    getWhatsappEventInvitations(buildFilteredPayload(next));
  };

  const guestDocs = invitations?.docs || [];
  const totalGuests = invitations?.totalDocs ?? guestDocs.length;
  const pageAccepted = guestDocs.filter((i) => i.status === "Accepted").length;
  const pagePending = guestDocs.filter((i) => i.status === "Unseen").length;
  const pageFailed = guestDocs.filter((i) => i.messageStatus === "failed").length;
  const statusChip = formData.status;

  return (
    <PlatformPage>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <PlatformPageHeader
          title="Guest list"
          subtitle={event?.title ? `${event.title} · Module 1 — RSVP & QR invitation` : "Invitations & delivery status"}
          actions={
            <PlatformButton
              variant="primary"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => downloadEventInvitations({ eventId: id })}
            >
              Export
            </PlatformButton>
          }
        />

        <PlatformStatsGrid>
          <PlatformStatCard
            label="Total guests"
            value={totalGuests}
            delta="Across all pages"
            icon={<PeopleOutlineIcon />}
            iconBg={platformColors.brandPale}
            iconColor={platformColors.brand}
          />
          <PlatformStatCard
            label="Confirmed"
            value={pageAccepted}
            delta="On this page"
            deltaTone="up"
            icon={<CheckCircleOutlineIcon />}
            iconBg="#D1FAE5"
            iconColor="#065F46"
          />
          <PlatformStatCard
            label="Pending"
            value={pagePending}
            delta="Awaiting response"
            icon={<HourglassEmptyIcon />}
            iconBg="#FEF3C7"
            iconColor="#92400E"
          />
          <PlatformStatCard
            label="Failed delivery"
            value={pageFailed}
            delta="On this page"
            deltaTone={pageFailed > 0 ? "down" : "up"}
            icon={<ErrorOutlineIcon />}
            iconBg="#FEE2E2"
            iconColor="#991B1B"
          />
        </PlatformStatsGrid>

        <PlatformCard
          title={event?.title ? `Guest list — ${event.title}` : "Guest list"}
          sx={{ mb: 2.5 }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 1.5 }} alignItems="stretch">
            <PlatformSearchBar
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              placeholder="Search guests by name or phone..."
            />
            <PlatformButton variant="ghost" onClick={handleClearFilter}>
              Clear
            </PlatformButton>
            <PlatformButton variant="primary" onClick={handleSearchFilter}>
              Search
            </PlatformButton>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <PlatformFilterChip
              label={`All (${totalGuests})`}
              active={statusChip === "any"}
              onClick={() => applyFilters({ status: "any", page: 1 })}
            />
            <PlatformFilterChip
              label="Confirmed"
              active={statusChip === "Accepted"}
              onClick={() => applyFilters({ status: "Accepted", page: 1 })}
            />
            <PlatformFilterChip
              label="Pending"
              active={statusChip === "Unseen"}
              onClick={() => applyFilters({ status: "Unseen", page: 1 })}
            />
            <PlatformFilterChip
              label="Declined"
              active={statusChip === "Rejected"}
              onClick={() => applyFilters({ status: "Rejected", page: 1 })}
            />
            <PlatformFilterChip
              label="Failed"
              active={formData.messageStatus === "failed"}
              badge={pageFailed > 0 ? pageFailed : null}
              onClick={() => applyFilters({ messageStatus: "failed", page: 1 })}
            />
          </Stack>
        </PlatformCard>

        <PlatformCard title="Filters" sx={{ mb: 2.5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Message Status</InputLabel>
                  <Select
                    value={messageStatus}
                    label="Message Status"
                    name="messageStatus"
                    onChange={handleChange}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { bgcolor: 'var(--platform-surface)', color: 'var(--primaryPurple)' } },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--platform-surface)',
                        '& fieldset': {
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--primaryPurple)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#333',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'var(--tickit-gray500)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                        '&.Mui-focused': {
                          color: 'var(--primaryPurple)',
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--primaryPurple)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                      }
                    }}
                  >
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="sent">Sent</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="delivered">Delivered</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="read">Read</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="failed">Failed</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="deleted">Deleted</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="not sent">Not Sent</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="any">Any</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Ticket Message Status</InputLabel>
                  <Select
                    value={ticketMessageStatus}
                    label="Ticket Message Status"
                    name="ticketMessageStatus"
                    onChange={handleChange}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { bgcolor: 'var(--platform-surface)', color: 'var(--primaryPurple)' } },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--platform-surface)',
                        '& fieldset': {
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--primaryPurple)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#333',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'var(--tickit-gray500)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                        '&.Mui-focused': {
                          color: 'var(--primaryPurple)',
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--primaryPurple)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                      }
                    }}
                  >
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="sent">Sent</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="delivered">Delivered</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="read">Read</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="failed">Failed</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="deleted">Deleted</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="not sent">Not Sent</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="any">Any</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    label="Status"
                    name="status"
                    onChange={handleChange}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { bgcolor: 'var(--platform-surface)', color: 'var(--primaryPurple)' } },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--platform-surface)',
                        '& fieldset': {
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--primaryPurple)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#333',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'var(--tickit-gray500)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                        '&.Mui-focused': {
                          color: 'var(--primaryPurple)',
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--primaryPurple)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                      }
                    }}
                  >
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="Unseen">Unseen</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="Accepted">Accepted</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="Rejected">Rejected</MenuItem>
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="any">Any</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    name="category"
                    onChange={handleChange}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { bgcolor: 'var(--platform-surface)', color: 'var(--primaryPurple)' } },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'var(--platform-surface)',
                        '& fieldset': {
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'var(--primaryPurple)',
                        },
                        '&:hover fieldset': {
                          borderColor: '#333',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'var(--tickit-gray500)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                        '&.Mui-focused': {
                          color: 'var(--primaryPurple)',
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'var(--primaryPurple)',
                        fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                      }
                    }}
                  >
                    <MenuItem sx={{ color: 'var(--primaryPurple)' }} value="any">Any</MenuItem>
                    {event?.categories?.map((cat) => (
                      <MenuItem key={cat.category} sx={{ color: 'var(--primaryPurple)' }} value={cat.category}>
                        {cat.category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyPress={handleSearchKeyPress}
                  placeholder="Search by name or phone number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'var(--platform-surface)',
                      '& fieldset': {
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--primaryPurple)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#333',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'var(--tickit-gray500)',
                      fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                      '&.Mui-focused': {
                        color: 'var(--primaryPurple)',
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: 'var(--primaryPurple)',
                      fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif',
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
                  <PlatformButton variant="ghost" onClick={handleClearFilter}>
                    Clear filters
                  </PlatformButton>
                  <PlatformButton variant="primary" onClick={handleSearchFilter}>
                    Apply filters
                  </PlatformButton>
                  <PlatformButton
                    variant="ghost"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadEventInvitations({ eventId: id })}
                  >
                    Export invitations
                  </PlatformButton>
                </Stack>
              </Grid>
            </Grid>
        </PlatformCard>

        <PlatformCard flush>
          {!isLoading ? (
            <TableContainer sx={platformTableContainerSx}>
              <Table sx={{ minWidth: 720, ...platformTableSx }} aria-label="invitations table">
                <TableHead>
                  <TableRow>
                    <TableCell>Guest</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Delivery</TableCell>
                    <TableCell>RSVP</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations?.docs?.map((invitation) => (
                    <InvitationRow
                      key={invitation._id}
                      invitation={invitation}
                      onInfoClick={handleInfoClick}
                      onManualAction={handleManualAction}
                      isProcessing={processingInvitationId === invitation._id}
                      eventStarted={eventStarted}
                    />
                  ))}
                </TableBody>
              </Table>
              {invitations?.docs?.length === 0 && (
                <PlatformEmptyState title="No invitations found" description="Try adjusting your filters or search query." />
              )}
            </TableContainer>
          ) : (
            <PlatformLoadingBlock />
          )}
          <Box sx={{ ...centered_flex_box, py: 2 }}>
            <Pagination
              count={invitations?.pages || 1}
              page={page}
              onChange={handlePageChange}
              sx={{
                "& .Mui-selected": {
                  bgcolor: `${platformColors.brandPale} !important`,
                  color: platformColors.brand,
                },
              }}
            />
          </Box>
        </PlatformCard>
      </motion.div>

      {/* Info Modal */}
      <Modal
        open={infoModalOpen}
        onClose={handleCloseInfoModal}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', sm: '600px' },
          maxWidth: '90vw',
          maxHeight: '90vh',
          outline: 'none',
          backgroundColor: 'var(--platform-surface)',
          borderRadius: 2,
          boxShadow: 24,
          border: '1px solid #e0e0e0',
          p: 3,
          overflow: 'auto'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'var(--primaryPurple)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
              Invitation Details
            </Typography>
            <IconButton
              onClick={handleCloseInfoModal}
              size="small"
              sx={{
                color: 'var(--primaryPurple)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {selectedInvitation && (
            <Box>
              {/* User Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'var(--primaryPurple)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  User Information
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  <strong style={{ color: 'var(--primaryPurple)' }}>Name:</strong> {selectedInvitation.userInfo?.name || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  <strong style={{ color: 'var(--primaryPurple)' }}>Phone Number:</strong> {selectedInvitation.userInfo?.phoneNumber || "N/A"}
                </Typography>
                {selectedInvitation.userInfo?.email && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                    <strong style={{ color: 'var(--primaryPurple)' }}>Email:</strong> {selectedInvitation.userInfo.email}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Ticket Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'var(--primaryPurple)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  Ticket Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedInvitation.ticket ? (
                    <>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                        Ticket Issued
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CancelIcon sx={{ color: 'var(--tickit-gray500)', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'var(--tickit-gray500)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                        No Ticket Issued
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Message Status Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'var(--primaryPurple)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  Invitation Message Status
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={selectedInvitation.messageStatus === "not sent" ? "Not Sent" : selectedInvitation.messageStatus}
                    color={
                      selectedInvitation.messageStatus === "failed" ? "error" :
                      selectedInvitation.messageStatus === "delivered" ? "success" :
                      selectedInvitation.messageStatus === "read" ? "info" :
                      selectedInvitation.messageStatus === "sent" ? "primary" :
                      selectedInvitation.messageStatus === "not sent" ? "warning" : "default"
                    }
                    size="small"
                    sx={{ textTransform: selectedInvitation.messageStatus === "not sent" ? "none" : "capitalize" }}
                  />
                </Box>
                {selectedInvitation.messageStatus === "failed" && selectedInvitation.messageInfo && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: '#c62828', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                      Failure Reason:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#c62828', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                      {selectedInvitation.messageInfo}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Ticket Message Status Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'var(--primaryPurple)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  Ticket Message Status
                </Typography>
                {selectedInvitation.ticketMessageStatus ? (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Chip
                        label={selectedInvitation.ticketMessageStatus === "not sent" ? "Not Sent" : selectedInvitation.ticketMessageStatus}
                        color={
                          selectedInvitation.ticketMessageStatus === "failed" ? "error" :
                          selectedInvitation.ticketMessageStatus === "delivered" ? "success" :
                          selectedInvitation.ticketMessageStatus === "read" ? "info" :
                          selectedInvitation.ticketMessageStatus === "sent" ? "primary" :
                          selectedInvitation.ticketMessageStatus === "not sent" ? "warning" : "default"
                        }
                        size="small"
                        sx={{ textTransform: selectedInvitation.ticketMessageStatus === "not sent" ? "none" : "capitalize" }}
                      />
                    </Box>
                    {selectedInvitation.ticketMessageStatus === "failed" && selectedInvitation.ticketMessageInfo && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ef5350' }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: '#c62828', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                          Failure Reason:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#c62828', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                          {selectedInvitation.ticketMessageInfo}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'var(--tickit-gray500)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                    N/A
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Additional Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'var(--primaryPurple)', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  Additional Information
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  <strong style={{ color: 'var(--primaryPurple)' }}>Category:</strong> {selectedInvitation.category || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  <strong style={{ color: 'var(--primaryPurple)' }}>Status:</strong> {selectedInvitation.status || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                  <strong style={{ color: 'var(--primaryPurple)' }}>Created:</strong> {moment(selectedInvitation.createdAt).format("MMM DD, YYYY HH:mm")}
                </Typography>
                {selectedInvitation.messageId && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                    <strong style={{ color: 'var(--primaryPurple)' }}>Message ID:</strong> {selectedInvitation.messageId}
                  </Typography>
                )}
                {selectedInvitation.ticketMessageId && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#333', fontFamily: 'TickitTwo, Helvetica, Arial, sans-serif' }}>
                    <strong style={{ color: 'var(--primaryPurple)' }}>Ticket Message ID:</strong> {selectedInvitation.ticketMessageId}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleCloseInfoModal}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  invitations: state?.records?.results?.invitations,
  isLoading: state?.records?.isLoading,
  event: state?.events?.results,
});

const mapDispatchToProps = {
  getWhatsappEventInvitations,
  downloadEventInvitations,
  manuallyAcceptInvitation,
  getEvent
};

export default connect(mapStateToProps, mapDispatchToProps)(EventInvitations);
